import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { WorkFlowStatus } from 'components/common/WorkFlowCard/types';
import { Comment, commentActionEnum } from 'redux/conductInspectionSlice';
import { RootStates } from 'redux/store';
import { apiUrl } from '../../constants/paths';

export enum ObservationTypeEnum {
  'POSITIVE' = 'POSITIVE',
  'NEGATIVE' = 'NEGATIVE',
}

export interface ISingleObservation {
  areaOfObservations: string;
  description: string;
  images: { docType: string; url: string }[];
  locations: string[];
  observationType: ObservationTypeEnum;
  isCloseTillRequired: boolean;
  closeTill: Date;
}

export interface IObservations {
  _id: string;
  projectId: string;
  displayId: string;
  subProject: { name: string; _id?: string };
  subTask: { name: string; _id?: string };
  endTask: { name: string; _id?: string };
  doer: string;
  inspector?: string;
  created_at?: string;
  updated_at?: string;
  observationDate?: string;
  status?: WorkFlowStatus;
  observations?: ISingleObservation[];
  observationInfo?: ISingleObservation;
}

export type ObservationTask = {
  _id: string;
  name: string;
  projectId: string;
  userId: string;
  inspector: string;
  doer: string;
  observationId: string;
  status: WorkFlowStatus;
  type: string;
  dueDate: string;
  created_at: string;
  updated_at: string;
  updatedBy: string;
  currentStatus: commentActionEnum;
  comments: Comment[];
};

export interface IObservationsReport {
  projectInfo: {
    projectDisplayId: string;
    projectName: string;
  };
  tasks: ObservationTask[];
  observationsInfo: IObservations;
}

export const observationApi = createApi({
  reducerPath: 'observationApi',
  refetchOnReconnect: true,
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootStates).auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  endpoints: (build) => ({
    createObservation: build.mutation({
      query: (body) => ({
        url: `${apiUrl.project}/${body.id}/observations`,
        method: 'POST',
        body: body,
      }),
    }),
    updateAddObservationPatch: build.mutation({
      query: (params) => ({
        url: `${apiUrl.project}/${params.projectId}/observations/${params.observationId}`,
        method: 'PATCH',
        body: params.body,
      }),
    }),
    getObservations: build.mutation({
      query: ({
        id,
        page = 1,
        limit = 16,
        status = '',
        filterBy = '',
        filterValue = '',
      }) => ({
        url: `${apiUrl.project}/${id}/observations?filterBy=${filterBy}&filterValue=${filterValue}&status=${status}&page=${page}&limit=${limit}`,
        method: 'get',
      }),
    }),
    getObservationById: build.mutation({
      query: (param) => ({
        url: `${apiUrl.project}/${param.projectId}/observations/records/${param.id}`,
        method: 'get',
      }),
    }),
    getLocations: build.mutation({
      query: (param) => ({
        url: `${apiUrl.project}/${param.id}/observations/locations`,
        method: 'get',
      }),
    }),
    getCounts: build.mutation({
      query: (param) => ({
        url: `${apiUrl.project}/${param.id}/observations/counts`,
        method: 'get',
      }),
    }),
    getTasksByType: build.mutation({
      query: (param) => ({
        url: `${apiUrl.project}/${param.projectId}/tasks?taskType=OBSERVATION&taskTypeId=${param.id}&status=ALL`,
        method: 'get',
        body: param.body,
      }),
    }),
    addComment: build.mutation({
      query: (param) => ({
        url: `${apiUrl.project}/${param.projectId}/tasks/${param.taskId}`,
        method: 'PATCH',
        body: param.body,
      }),
    }),
    getReport: build.mutation({
      query: (param) => ({
        url: `/observations/${param.id}/reports`,
        method: 'get',
      }),
    }),
  }),
});

export const {
  useCreateObservationMutation,
  useUpdateAddObservationPatchMutation,
  useGetObservationsMutation,
  useGetLocationsMutation,
  useGetCountsMutation,
  useGetObservationByIdMutation,
  useGetTasksByTypeMutation,
  useAddCommentMutation,
  useGetReportMutation,
} = observationApi;
